import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEFAULT_STATUSES, entryKey, statusesAsMap } from "../constants";
import { computeStats, calcSalary } from "../controllers/salary";
import { computeTaxes } from "../controllers/taxes";
import { daysInMonth, weekdayMon0, ddKey, todayParts } from "../utils/dates";
import { trackEvent } from "../utils/analytics";
import { exportDemoTimesheet } from "../api";
import HomePayCalculator from "./HomePayCalculator";
import "./LandingTimesheetDemo.css";

const DEMO_TAX_CFG = {
  ref_values: [
    { year: 2025, mzp: 85000, mrp: 3932 },
    { year: 2026, mzp: 85000, mrp: 4325 },
  ],
  deductions: [
    { key: "opv_pct", label: "Вычет ОПВ", type: "percent_gross", value: 10, unit: "tenge", enabled: true },
    { key: "vosms_pct", label: "Вычет ВОСМС", type: "percent_gross", value: 2, unit: "tenge", enabled: true },
    { key: "std_mrp", label: "Стандартный вычет (30 МРП)", type: "absolute", value: 30, unit: "mrp", enabled: true },
  ],
  taxes: [
    { key: "opv", label: "ОПВ", rate: 10, base: "gross", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
    { key: "vosms", label: "ВОСМС", rate: 2, base: "gross", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 20, deduction_keys: [] },
    { key: "ipn", label: "ИПН", rate: 10, base: "after_deductions", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: ["opv_pct", "vosms_pct", "std_mrp"] },
    { key: "so", label: "СО", rate: 5, base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 1, max_base_mzp: 7, deduction_keys: ["opv_pct"] },
    { key: "oosms", label: "ООСМС", rate: 3, base: "gross", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 40, deduction_keys: [] },
    { key: "opvr", label: "ОПВР", rate: 3.5, base: "gross", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
    { key: "sn", label: "Соцналог", rate: 6, base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: ["opv_pct", "vosms_pct"] },
  ],
};

const STORAGE_KEY = "tabel_landing_demo_v1";
const MAX_EMPLOYEES = 3;
const MONTH_KEYS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const WD_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("ru-RU");

function buildCalendarDays(year, month) {
  const days = {};
  const total = daysInMonth(year, month);
  for (let d = 1; d <= total; d++) {
    const wd = weekdayMon0(year, month, d);
    days[ddKey(d)] = wd >= 5 ? "weekend" : "work";
  }
  return days;
}

function defaultState(t) {
  const today = todayParts();
  return {
    employees: [{
      id: "demo_1",
      name: t("landing.demo.default_name"),
      salary: 300000,
      role: "director",
    }],
    entries: {},
    selectedId: "demo_1",
    year: today.year,
    month: today.month,
  };
}

function loadState(t) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState(t);
    const parsed = JSON.parse(raw);
    if (!parsed?.employees?.length) return defaultState(t);
    return {
      ...defaultState(t),
      ...parsed,
      selectedId: parsed.selectedId || parsed.employees[0]?.id,
    };
  } catch {
    return defaultState(t);
  }
}

export default function LandingTimesheetDemo({ registerHref = "/register", onRegisterClick }) {
  const { t } = useTranslation();
  const [state, setState] = useState(() => loadState(t));
  const [touched, setTouched] = useState(false);
  const [compact, setCompact] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [exportError, setExportError] = useState("");
  const [newName, setNewName] = useState("");
  const [newSalary, setNewSalary] = useState(250000);
  const [showAdd, setShowAdd] = useState(false);

  const statuses = DEFAULT_STATUSES;
  const statusMap = useMemo(() => statusesAsMap(statuses), [statuses]);

  const { employees, entries, selectedId, year, month } = state;
  const calendarDays = useMemo(() => buildCalendarDays(year, month), [year, month]);
  const total = daysInMonth(year, month);
  const today = todayParts();
  const isCurrentMonth = today.year === year && today.month === month;
  const todayDay = isCurrentMonth ? today.day : null;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore */ }
  }, [state]);

  const setPartial = useCallback((patch) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  const markTouched = () => {
    if (!touched) {
      setTouched(true);
      trackEvent("demo_use", { label: "first_click" });
    }
  };

  const selected = employees.find((e) => e.id === selectedId) || employees[0];

  const payroll = useMemo(() => {
    if (!selected) return null;
    const stats = computeStats(entries, selected.id, calendarDays, statuses);
    const sal = calcSalary({
      monthlySalary: Number(selected.salary) || 0,
      normWorkDays: stats.planned,
      paidDays: stats.paidDays,
      fixedTotal: stats.fixedTotal,
    });
    const taxes = computeTaxes(sal.gross, DEMO_TAX_CFG, null, { year });
    return { stats, sal, taxes };
  }, [selected, entries, calendarDays, statuses, year]);

  const onCellChange = (empId, day, status) => {
    markTouched();
    trackEvent("demo_use", { label: "cell" });
    const key = entryKey(empId, day);
    setState((s) => {
      const next = { ...s.entries };
      if (!status) delete next[key];
      else next[key] = { status };
      return { ...s, entries: next };
    });
  };

  const onAddEmployee = (e) => {
    e.preventDefault();
    if (employees.length >= MAX_EMPLOYEES) return;
    const name = (newName || t("landing.demo.new_employee_name")).trim();
    const salary = Math.max(0, Number(newSalary) || 0);
    const id = `demo_${Date.now().toString(36)}`;
    setState((s) => ({
      ...s,
      employees: [...s.employees, { id, name, salary, role: "employee" }],
      selectedId: id,
    }));
    setNewName("");
    setNewSalary(250000);
    setShowAdd(false);
    markTouched();
    trackEvent("demo_use", { label: "add_employee" });
  };

  const onExport = async (format) => {
    markTouched();
    trackEvent("demo_use", { label: `export_${format}` });
    setExportError("");
    setExporting(format);
    try {
      await exportDemoTimesheet({
        year,
        month,
        company_name: "Tabel.kz — пример",
        employees: employees.map((e) => ({
          user_id: e.id,
          name: e.name,
          position: t(`roles.${e.role || "employee"}`),
        })),
        entries,
        calendar_days: calendarDays,
      }, format);
    } catch {
      setExportError(t("landing.demo.export_error"));
    } finally {
      setExporting(null);
    }
  };

  const resetDemo = () => {
    setState(defaultState(t));
    setTouched(false);
    setExportError("");
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  };

  const dayHeaders = [];
  for (let d = 1; d <= total; d++) {
    const wd = weekdayMon0(year, month, d);
    const calStatus = calendarDays[ddKey(d)] || (wd >= 5 ? "weekend" : "work");
    dayHeaders.push({ d, wd, calStatus, isToday: d === todayDay });
  }

  return (
    <section id="demo" className="landing-demo-section py-5 bg-light" style={{ scrollMarginTop: 80 }}>
      <div className="container py-2">
        <div className="text-center mb-4">
          <span className="badge bg-primary mb-2">{t("landing.demo.badge")}</span>
          <h2 className="fw-bold mb-2" data-testid="landing-demo-title">{t("landing.demo.title")}</h2>
          <p className="text-muted mx-auto mb-0" style={{ maxWidth: 640 }}>{t("landing.demo.subtitle")}</p>
        </div>

        {touched && (
          <div className="alert alert-warning border-0 shadow-sm d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 landing-demo-nudge">
            <div className="small">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {t("landing.demo.nudge")}
            </div>
            <Link
              to={registerHref}
              className="btn btn-primary btn-sm fw-semibold"
              onClick={onRegisterClick}
              data-testid="landing-demo-nudge-register"
            >
              {t("landing.demo.save_cta")}
            </Link>
          </div>
        )}

        {exportError && (
          <div className="alert alert-danger py-2 small mb-3">
            <i className="bi bi-exclamation-circle me-2"></i>
            {exportError}
          </div>
        )}

        <div data-testid="landing-demo-timesheet">
          <div className="landing-demo-timesheet-panel">
          {/* Toolbar — как в /timesheet */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h4 className="fw-bold mb-0">{t("timesheet.title")}</h4>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ width: 150 }}
                value={month}
                onChange={(e) => setPartial({ month: Number(e.target.value) })}
                data-testid="demo-ts-month"
              >
                {MONTH_KEYS.map((mk, i) => (
                  <option key={mk} value={i}>{t(`months.${mk}`)}</option>
                ))}
              </select>
              <select
                className="form-select form-select-sm"
                style={{ width: 100 }}
                value={year}
                onChange={(e) => setPartial({ year: Number(e.target.value) })}
                data-testid="demo-ts-year"
              >
                {[year - 1, year, year + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <div className="btn-group" role="group" aria-label={t("timesheet.export_title")}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => onExport("xlsx")}
                  disabled={!!exporting}
                  data-testid="demo-ts-export-xlsx"
                  title={t("timesheet.export_excel_title")}
                >
                  {exporting === "xlsx" ? (
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-file-earmark-excel me-1"></i>
                  )}
                  Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => onExport("pdf")}
                  disabled={!!exporting}
                  data-testid="demo-ts-export-pdf"
                  title={t("timesheet.export_pdf_title")}
                >
                  {exporting === "pdf" ? (
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-file-earmark-pdf me-1"></i>
                  )}
                  PDF
                </button>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${compact ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setCompact(!compact)}
                title={t("timesheet.compact_title")}
                data-testid="demo-ts-compact-toggle"
              >
                <i className="bi bi-arrows-angle-contract"></i>
              </button>
              <button type="button" className="btn btn-sm btn-link text-muted" onClick={resetDemo}>
                {t("landing.demo.reset")}
              </button>
            </div>
          </div>

          <p className="small text-muted mb-2">
            <i className="bi bi-info-circle me-1"></i>
            {t("landing.demo.example_note")}
          </p>

          {/* Легенда статусов */}
          <div className="d-flex flex-wrap gap-2 mb-3 small">
            {statuses.map((s) => (
              <span key={s.key} className="d-inline-flex align-items-center gap-1">
                <span className="status-pill" style={{ background: s.color }}>{s.short}</span>
                <span className="text-muted">{s.label}</span>
              </span>
            ))}
          </div>

          {/* Таблица — те же классы, что в кабинете */}
          <div className="card card-soft">
            <div className="text-muted small px-3 pt-2 d-md-none">
              <i className="bi bi-arrow-left-right me-1"></i>
              {t("timesheet.scroll_hint")}
            </div>
            <div className="ts-scroll" data-testid="demo-ts-scroll">
              <table
                className={`table table-bordered align-middle mb-0 ts-grid ${compact ? "ts-compact" : ""}`}
                data-testid="demo-timesheet-table"
              >
                <thead className="table-light">
                  <tr>
                    <th className="ts-name-col">{t("timesheet.th_employee")}</th>
                    {dayHeaders.map((h) => (
                      <th
                        key={h.d}
                        className={`text-center ts-day-col ${h.isToday ? "ts-today-col" : ""}`}
                        style={{
                          background: h.isToday ? undefined : (
                            h.calStatus === "weekend" ? "#f1f3f6"
                              : h.calStatus === "holiday" ? "#ffe7e7"
                                : undefined
                          ),
                        }}
                      >
                        <div className="ts-day-num">
                          {h.isToday && (
                            <i className="bi bi-arrow-down-short ts-today-arrow" aria-hidden="true"></i>
                          )}
                          {h.d}
                        </div>
                        <div className="ts-day-wd">{t(`weekdaysShort.${WD_KEYS[h.wd]}`)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr
                      key={emp.id}
                      data-testid={`demo-ts-row-${emp.id}`}
                      className={emp.id === selectedId ? "table-active" : ""}
                      onClick={() => setPartial({ selectedId: emp.id })}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="ts-name-col">
                        <div className="fw-semibold small">{emp.name}</div>
                        <div className="text-muted ts-role">{t(`roles.${emp.role || "employee"}`)}</div>
                      </td>
                      {dayHeaders.map((h) => {
                        const e = entries[entryKey(emp.id, h.d)];
                        const calCls = h.calStatus === "weekend" ? "weekend"
                          : h.calStatus === "holiday" ? "holiday"
                            : h.calStatus === "short" ? "short" : "";
                        const status = e?.status;
                        const bg = status ? statusMap[status]?.color : undefined;
                        const fg = status ? "white" : undefined;
                        return (
                          <td
                            key={h.d}
                            className={`ts-cell-wrap ${h.isToday ? "ts-today-cell" : ""}`}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <select
                              className={`timesheet-cell ${calCls}`}
                              style={{ background: bg || undefined, color: fg || undefined }}
                              value={status || ""}
                              onChange={(ev) => onCellChange(emp.id, h.d, ev.target.value)}
                              data-testid={`demo-ts-cell-${emp.id}-${h.d}`}
                            >
                              <option value="">·</option>
                              {statuses.map((s) => (
                                <option key={s.key} value={s.key}>{s.short}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
            {employees.length < MAX_EMPLOYEES && !showAdd && (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowAdd(true)}>
                <i className="bi bi-person-plus me-1"></i>
                {t("landing.demo.add_employee")}
              </button>
            )}
            {showAdd && (
              <form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={onAddEmployee}>
                <div>
                  <label className="form-label small mb-0">{t("common.fullname")}</label>
                  <input
                    className="form-control form-control-sm"
                    value={newName}
                    onChange={(ev) => setNewName(ev.target.value)}
                    placeholder={t("landing.demo.new_employee_name")}
                    style={{ width: 140 }}
                  />
                </div>
                <div>
                  <label className="form-label small mb-0">{t("landing.demo.salary")}</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={newSalary}
                    onChange={(ev) => setNewSalary(ev.target.value)}
                    min={0}
                    step={10000}
                    style={{ width: 120 }}
                  />
                </div>
                <button type="submit" className="btn btn-sm btn-primary">{t("common.add")}</button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdd(false)}>
                  {t("common.cancel")}
                </button>
              </form>
            )}
          </div>
          </div>

          {/* Расчёт за месяц + калькулятор рядом */}
          <div className="row g-4 landing-demo-tools-row" id="calc">
            {selected && payroll && (
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100 landing-demo-payroll-card">
                  <div className="card-body p-4 d-flex flex-column">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-cash-stack text-success fs-4 me-2"></i>
                      <div>
                        <h5 className="fw-bold mb-0">{t("landing.demo.payroll_title")}</h5>
                        <div className="small text-muted">{selected.name}</div>
                      </div>
                    </div>

                    <div className="row g-2 mb-3 small">
                      <div className="col-4">
                        <div className="text-muted">{t("landing.demo.norm_days")}</div>
                        <div className="fw-semibold">{payroll.stats.planned}</div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted">{t("landing.demo.paid_days")}</div>
                        <div className="fw-semibold">{payroll.stats.paidDays}</div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted">{t("landing.demo.accrued")}</div>
                        <div className="fw-semibold">{fmt(payroll.sal.gross)} ₸</div>
                      </div>
                    </div>

                    <div className="small flex-grow-1">
                      {["opv", "vosms", "ipn"].map((k) => payroll.taxes.byTax[k] && (
                        <div key={k} className="d-flex justify-content-between align-items-baseline gap-2 py-1">
                          <span className="text-muted">{t(`landing.calc.${k}`)}</span>
                          <strong className="text-nowrap">−{fmt(payroll.taxes.byTax[k].amount)} ₸</strong>
                        </div>
                      ))}
                      <div className="text-muted mt-2 mb-1">{t("landing.calc.employer")}</div>
                      {["so", "oosms", "opvr", "sn"].map((k) => payroll.taxes.byTax[k] && (
                        <div key={k} className="d-flex justify-content-between align-items-baseline gap-2 py-1">
                          <span className="text-muted">{t(`landing.calc.${k}`)}</span>
                          <span className="text-nowrap fw-semibold text-secondary">
                            {fmt(payroll.taxes.byTax[k].amount)} ₸
                          </span>
                        </div>
                      ))}
                    </div>

                    <hr />
                    <div className="d-flex align-items-baseline">
                      <span className="text-muted">{t("landing.calc.net_label")}</span>
                      <strong className="ms-auto text-success" style={{ fontSize: 24 }}>
                        {fmt(payroll.taxes.net)} ₸
                      </strong>
                    </div>
                    <p className="small text-muted mt-3 mb-0">{t("landing.demo.payroll_note")}</p>
                  </div>
                </div>
              </div>
            )}
            <div className={selected && payroll ? "col-lg-6" : "col-lg-8 mx-auto"}>
              <HomePayCalculator />
            </div>
          </div>

          <div className="text-center mt-4 pt-2">
            <p className="text-muted small mb-3">{t("landing.demo.footer_note")}</p>
            <Link
              to={registerHref}
              className="btn btn-primary btn-lg px-4"
              onClick={onRegisterClick}
              data-testid="landing-demo-register"
            >
              <i className="bi bi-rocket-takeoff me-2"></i>
              {t("landing.demo.save_cta")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
